import { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface MedsModalProps {
  onSubmit: (meds: string[]) => void;
}

export function MedsModal({ onSubmit }: MedsModalProps) {
  const [meds, setMeds] = useState<string[]>([]);
  const [input, setInput] = useState('');

  function addMed() {
    const trimmed = input.trim();
    if (trimmed && !meds.includes(trimmed)) {
      setMeds([...meds, trimmed]);
      setInput('');
    }
  }

  function removeMed(med: string) {
    setMeds(meds.filter((m) => m !== med));
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMed();
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className=""
    >
      <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4">
        <p className="text-sm font-medium mb-3">
          Any current medications or supplements?
        </p>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Levothyroxine, Vitamin D..."
            className="flex-1 bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2 text-sm
              text-text-primary placeholder:text-text-muted outline-none focus:border-accent"
          />
          <button
            onClick={addMed}
            disabled={!input.trim()}
            className="px-3 py-2 rounded-lg bg-accent text-white text-sm
              hover:bg-accent-light transition-colors disabled:opacity-30"
          >
            Add
          </button>
        </div>

        {meds.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {meds.map((med) => (
              <span
                key={med}
                className="flex items-center gap-1 px-2.5 py-1 bg-bg-elevated rounded-full text-xs text-text-secondary"
              >
                {med}
                <button onClick={() => removeMed(med)} className="text-text-muted hover:text-low">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => onSubmit([])}
            className="flex-1 py-2.5 rounded-lg border border-bg-tertiary text-text-secondary text-sm
              hover:border-accent/40 transition-colors"
          >
            None
          </button>
          <button
            onClick={() => onSubmit(meds)}
            disabled={meds.length === 0}
            className="flex-1 py-2.5 rounded-lg bg-accent text-white text-sm font-medium
              hover:bg-accent-light transition-colors disabled:opacity-30"
          >
            Continue
          </button>
        </div>
      </div>
    </motion.div>
  );
}
